import React, { useEffect } from 'react';

import { NetworkStatus } from '@apollo/client';
import { useGraphQLMutation, useGraphQLQuery } from '@betterment/graphql';
import { snakeizeKeys } from '@betterment/js-runtime';
import { Box, Button, Card, Separator, Shimmer } from '@betterment/samba-react';
import { NoResults } from '@betterment/shared-components/src/components/activity/components/NoResults';
import * as Sentry from '@sentry/browser';
import { useParams } from 'react-router-dom';

import { CSVDownloadBanner, STOP_POLLING_STATUSES } from './components/CSVDownloadBanner/Banner';
import { FilterRow } from './components/FilterRow';
import { Table } from './components/Table';
import { CreateClientActivityCsvExportDocument } from './graphql/mutation.createCSVExport.generated';
import {
  ActivityItemsDocument,
  ActivityItemsQuery,
  ActivityItemsQueryVariables,
} from './graphql/query.activityItems.generated';
import {
  ClientActivityCsvExportDocument,
  ClientActivityCsvExportQuery,
} from './graphql/query.clientActivityCSVExport.generated';
import { useFilterOptions } from './hooks';

import { ActivityAdvisorType, DateRangeOption } from '../../graphql/schema';
import { IdempotencyProviderWithFeatureRegistry } from '../../providers/IdempotencyProviderWithFeatureRegistry';
import { safeDateString } from '../../utils/date';
import { buildAppPath } from '../../utils/path-helpers';
import { Header } from '../Household/Header';
import { Layout } from '../InstitutionalApp/Layout';

const INITIAL_ROW_COUNT = 50;
const FETCH_MORE_COUNT = 25;

const ActivityTableSkeleton: React.FC<{ rowCount: number }> = ({ rowCount }) => {
  return (
    <>
      {Array.from({ length: rowCount }).map((_, index) => (
        <Box
          key={`shimmer-row-${index}`} // eslint-disable-line react/no-array-index-key
          css={{
            px: 'space.300',
            py: 'space.200',
            display: 'grid',
            gap: 'space.200',
            gridTemplateAreas: `"1 2 3 4"`,
            gridTemplateColumns: '1fr 1fr 2fr 1fr',
            alignItems: 'center',
          }}
        >
          {['1', '2', '3', '4'].map(area => (
            <Box key={area} css={{ gridArea: area }}>
              <Shimmer
                css={{
                  height: 'size.300',
                  borderRadius: 'radius.100',
                }}
              />
            </Box>
          ))}
        </Box>
      ))}
    </>
  );
};

export const ActivityPage: React.FC<{ householdId?: string }> = ({ householdId }) => {
  const isClientPageFilter = Boolean(householdId);
  const {
    filterOptions,
    filters,
    setFilters,
    loading: isFiltersLoading,
    error: filterError,
  } = useFilterOptions({ householdId });

  const shouldSkipOnDateChange =
    // @ts-expect-error TS18048 violates strict null checks
    filters?.timeframe.value === DateRangeOption.Custom && (!filters?.startTimeInclusive || !filters?.endTimeExclusive);
  const shouldSkipCall = !filters || shouldSkipOnDateChange;

  const advisorType = isClientPageFilter ? ActivityAdvisorType.All : filters?.advisorType.value;
  const tableFilters = {
    advisorType,
    legalAccountTypes: filters?.legalAccountType.map(f => f.value),
    // @ts-expect-error TS18048 violates strict null checks
    dateRange: filters?.timeframe.value,
    activityTypes: filters?.transactionType.filter(f => String(f.value) !== 'all').map(f => f.value),
    clientIds: filters?.clients.map(f => f.value),
    startTimeInclusive: safeDateString(filters?.startTimeInclusive) ?? undefined,
    endTimeExclusive: safeDateString(filters?.endTimeExclusive) ?? undefined,
  };

  const { data, networkStatus, error, fetchMore } = useGraphQLQuery<ActivityItemsQuery, ActivityItemsQueryVariables>(
    ActivityItemsDocument,
    {
      variables: {
        first: INITIAL_ROW_COUNT,
        filters: tableFilters,
      },
      notifyOnNetworkStatusChange: true,
      skip: shouldSkipCall,
      fetchPolicy: 'cache-and-network',
    }
  );

  const pageInfo = data?.activityConnection.pageInfo;
  const nodes = data?.activityConnection.edges.map(edge => edge.node);
  const isNodesEmpty = (nodes?.length ?? 0) === 0;

  const isFetchingInitialData = networkStatus === NetworkStatus.loading;
  const isFetchingMoreData = networkStatus === NetworkStatus.fetchMore;
  const hasFiltersChanged = networkStatus === NetworkStatus.setVariables;

  const handleLoadMore = async () => {
    if (!pageInfo?.hasNextPage || !pageInfo?.endCursor) return;
    await fetchMore({
      variables: { first: FETCH_MORE_COUNT, after: pageInfo.endCursor },
    });
  };

  const [createAdvisorExport] = useGraphQLMutation(CreateClientActivityCsvExportDocument);

  const {
    data: csvExportData,
    startPolling,
    stopPolling,
    refetch: refetchBannerStatus,
  } = useGraphQLQuery<ClientActivityCsvExportQuery>(ClientActivityCsvExportDocument, {
    fetchPolicy: 'network-only', // only fetch the latest data
    skip: isClientPageFilter,
  });

  const downloadCSV = async () => {
    if (isClientPageFilter) {
      window.open(buildAppPath(`/unified_client_activity_csv.csv`, snakeizeKeys(tableFilters) || ''), '_blank');
    } else {
      await createAdvisorExport({
        variables: { input: { filters: tableFilters } },
      });
      await refetchBannerStatus();
    }
  };

  const isLoading = !csvExportData;
  const activityCSVReport = csvExportData?.clientActivityCsvExport;

  useEffect(() => {
    if (activityCSVReport && STOP_POLLING_STATUSES.includes(activityCSVReport.status)) {
      stopPolling();
    }
  }, [activityCSVReport, stopPolling]);

  return (
    <Box css={{ bg: 'background' }}>
      {!isClientPageFilter && !isLoading && activityCSVReport && (
        <CSVDownloadBanner
          startPolling={startPolling}
          activityCSVReportStatus={activityCSVReport.status}
          refetchBannerStatus={refetchBannerStatus}
        />
      )}
      <Card css={{ width: '100%', gap: 'space.300', pb: 'space.300' }}>
        <Card.Header css={{ px: 'space.300', pt: 'space.300' }}>
          {!isFiltersLoading && filterOptions && filters && (
            <FilterRow
              options={filterOptions}
              current={filters}
              onChange={setFilters}
              isClientPageFilter={isClientPageFilter}
              downloadCSV={downloadCSV}
            />
          )}
        </Card.Header>

        <Card.Content>
          {isFetchingInitialData || hasFiltersChanged || (!filters && !filterError) ? (
            <>
              <Separator css={{ fullWidthWithInset: 'space.300' }} />
              <ActivityTableSkeleton rowCount={INITIAL_ROW_COUNT} />
            </>
          ) : (
            <>
              <Table activityItems={nodes ?? []} />
              <Separator css={{ fullWidthWithInset: 'space.300' }} />
            </>
          )}
          {!error && !filterError && !isFiltersLoading && !isFetchingInitialData && isNodesEmpty && (
            <NoResults title="No activity found" description="Adjust your filters to find transactions" />
          )}
          {(error || filterError) && (
            <NoResults
              title="Unable to complete request."
              description="We're sorry, we're working to resolve this issue. Please try again later. If the issue continues, contact us for help at support@bettermentadvisorsolutions.com."
            />
          )}
        </Card.Content>

        <Card.Footer css={{ alignItems: 'left', pl: 'space.400' }}>
          {!isFetchingInitialData && !isNodesEmpty && pageInfo?.hasNextPage && (
            <Button
              variant="solidSecondary"
              loading={isFetchingMoreData}
              onClick={() => {
                handleLoadMore().catch(Sentry.captureException);
              }}
              disabled={!pageInfo?.hasNextPage || isFetchingInitialData}
            >
              Load more activities
            </Button>
          )}
        </Card.Footer>
      </Card>
    </Box>
  );
};

export const HouseholdActivityWrapper: React.FC = () => {
  const { householdId } = useParams<{ householdId?: string }>();
  return (
    <IdempotencyProviderWithFeatureRegistry featureName="clientEnvelope">
      <ActivityPage householdId={householdId} />
    </IdempotencyProviderWithFeatureRegistry>
  );
};

export const HouseholdActivity: React.FC = () => {
  const { householdId } = useParams<{ householdId?: string }>();
  return (
    <Layout active="clients">
      <Layout.Header>
        <Header />
      </Layout.Header>
      <Layout.Content>
        <IdempotencyProviderWithFeatureRegistry featureName="clientEnvelope">
          <ActivityPage householdId={householdId} />
        </IdempotencyProviderWithFeatureRegistry>
      </Layout.Content>
    </Layout>
  );
};
